import { Alert, Divider } from '@design-system';
import { captureError } from '@utils/capture';
import { buildSubscriptions } from '@utils/subscriptions';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';

import {
  LazyCloudflareTurnstile,
  useResetTurnstile,
} from '../../../components/Captcha/LazyCloudflareTurnstile';
import { EmbeddedContainer } from '../../../components/Containers/DrawerContainer/EmbeddedContainer';
import UnderlinedLink from '../../../components/UnderlinedLink';
import { pathFor } from '../../../helpers/links';
import { useGeneralSubscription } from '../../../hooks/useGeneralSubscription';
import { ProductPurchaseDetails } from '../../../types/Purchasing';
import { ButtonLocation } from '../../../types/Subscriptions';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { EnquiryForm, EnquiryFormData } from '../components/EnquiryForm';
import { ReviewItem } from '../components/ReviewItem';
import type {
  Enquiry,
  EnquiryGateway,
  EnquirySource,
  MakeEnquiryError,
} from '../types/Enquiries';

type Props = {
  enquiryGateway: EnquiryGateway;
  onSubmit?: () => void;
  onSuccess?: () => void;
  source: EnquirySource;
  product?: Pick<ProductType, 'id' | 'title'>;
  thumbnail: {
    filename: string;
    focus: string;
  };
  productPrice?: number;
  productPurchaseDetails?: ProductPurchaseDetails;
  hideTitle?: boolean;
  location: ButtonLocation;
  artists: { name: string; internalId: string | null }[];
};

export const EnquiryFormScreen: React.FC<Props> = ({
  artists,
  enquiryGateway,
  hideTitle,
  location,
  onSubmit,
  onSuccess,
  product,
  productPrice,
  productPurchaseDetails,
  source,
  thumbnail,
}) => {
  const { t } = useTranslation('enquiries');
  const [emitter] = useAnalyticsEmitter();

  const [error, setError] = useState<MakeEnquiryError | null>(null);
  const [loading, setLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState<string>();
  const { turnstileRef, turnstileReset } = useResetTurnstile();

  const productId = product?.id?.[0]?.id;
  const productRef = productPurchaseDetails?.sku?.substring(0, 14);

  const subscriptions = buildSubscriptions({
    includeGeneral: true,
    artists,
    productId: productId || '',
    productRef: productRef || '',
    productTitle: product?.title || '',
    isPreOrder: false,
  });

  const {
    loading: loadingSubscription,
    start,
    subscribed,
  } = useGeneralSubscription({
    buttonLocation: location,
    source,
    subscriptions,
  });

  const handleSubmit = async (values: EnquiryFormData) => {
    try {
      onSubmit?.();

      setLoading(true);
      setError(null);

      const enquiry: Enquiry = {
        name: values.name,
        email: values.email,
        message: values.message,
        productValue: productPrice,
        source,
        sourceUrl: source !== undefined ? window.location.href : undefined,
      };

      const response = await enquiryGateway.submit(enquiry);

      setLoading(false);

      if (response.error !== undefined) {
        setError(response.error.code);
        return;
      }

      onSuccess?.();

      if (!subscribed) {
        await start(values.email, captchaToken);
      }
    } catch (err) {
      captureError(err);

      turnstileReset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmbeddedContainer embedded>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-4">
          {!hideTitle ? (
            <div className="w-full">
              <p className="text-md font-bold md:text-lg">
                {t('drawers.formScreen.title')}
              </p>
            </div>
          ) : null}

          {error !== null && (
            <Alert status="error">{t('form.errors.generic')}</Alert>
          )}
          {product && productPurchaseDetails ? (
            <div className="flex flex-col space-y-4 rounded-sm border border-divider/20 p-4">
              <ReviewItem
                allowMultiples={false}
                artist={productPurchaseDetails.artist || ''}
                imageFocus={thumbnail?.focus}
                imageUrl={thumbnail?.filename}
                price={productPrice}
                quantity={1}
                title={product.title}
              />
            </div>
          ) : null}

          <div className="flex w-full flex-col">
            <EnquiryForm
              loading={loading || loadingSubscription}
              onSubmit={handleSubmit}
            />

            <span className="mt-3 w-full text-center text-xs text-neutral-4">
              <Trans
                components={{
                  1: (
                    <UnderlinedLink
                      href={pathFor({ page: 'TERMS_AND_CONDITIONS' })}
                      target="_blank"
                    />
                  ),
                  2: (
                    <UnderlinedLink
                      href={pathFor({ page: 'PRIVACY_POLICY' })}
                      target="_blank"
                    />
                  ),
                }}
                i18nKey="drawers.formScreen.responseNote"
                t={t}
              />
            </span>

            <LazyCloudflareTurnstile
              actionName="sign_up_and_subscribe"
              className="mt-3"
              onError={() => setCaptchaToken(undefined)}
              onExpire={() => setCaptchaToken(undefined)}
              onSuccess={setCaptchaToken}
              turnstileRef={turnstileRef}
            />
          </div>
        </div>

        <Divider />

        <div className="w-full text-center">
          <Trans i18nKey="drawers.formScreen.meetingLink" t={t}>
            Want to speak to an art advisor?&nbsp;
            <UnderlinedLink
              href="https://meetings.hubspot.com/ngaio/enquire-now-private-viewing-room"
              onClick={() => emitter('enquire step', 'book meeting clicked')}
              target="_blank"
            >
              Book a meeting
            </UnderlinedLink>
          </Trans>
        </div>
      </div>
    </EmbeddedContainer>
  );
};
